/**
 * Education360 - independent education platform application root.
 * Mounted at /education360/* by App.js. Does not affect THOM360 routing.
 *
 * Preview URL structure (path-based tenant simulation):
 *   /education360                            -> Education360 platform landing
 *   /education360/:school                    -> School-branded login
 *   /education360/:school/executive          -> Executive dashboard
 *   /education360/:school/students, etc.
 *
 * Production (subdomain-based, e.g. highlands.education360.thomdi.com):
 *   The routing abstraction below already treats :school as the tenant slug.
 *   A production subdomain resolver simply feeds the sub-label as `school`
 *   without changing any component code.
 */
import React from "react";
import {
  Routes, Route, useParams, Navigate, useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import "./theme.css";
import "./landing.css";
import { AuthProvider, useAuth } from "./lib/auth";
import Education360Landing from "./pages/Education360Landing";
import SchoolLogin from "./pages/SchoolLogin";
import PortalLayout from "./layouts/PortalLayout";

import Executive from "./pages/Executive";
import ExecutiveIntelligence from "./pages/ExecutiveIntelligence";
import StudentLife360 from "./pages/StudentLife360";
import AIExecutive from "./pages/AIExecutive";
import Students from "./pages/Students";
import StudentDetail from "./pages/StudentDetail";
import Academics from "./pages/Academics";
import Attendance from "./pages/Attendance";
import Timetable from "./pages/Timetable";
import Assessments from "./pages/Assessments";
import ReportCards from "./pages/ReportCards";
import Finance from "./pages/Finance";
import AdmissionsHub from "./pages/admissions360/AdmissionsHub";
import AdmissionsWorkspace from "./pages/AdmissionsWorkspace";
import Clinic from "./pages/Clinic";
import AdmissionsPipeline from "./pages/admissions360/AdmissionsPipeline";
import ApplicantDetail from "./pages/admissions360/ApplicantDetail";
import EnquiriesInbox from "./pages/admissions360/EnquiriesInbox";
import PublicEnquiry from "./pages/admissions360/PublicEnquiry";
import PublicApply from "./pages/admissions360/PublicApply";
import ApplicantPortal from "./pages/admissions360/ApplicantPortal";
import Boarding from "./pages/Boarding";
import Transport from "./pages/Transport";
import Medical from "./pages/Medical";
import Behaviour from "./pages/Behaviour";
import LibraryPage from "./pages/Library";
import Sports from "./pages/Sports";
import HR from "./pages/HR";
import StaffDirectory from "./pages/StaffDirectory";
import Notifications from "./pages/Notifications";
import Documents from "./pages/Documents";
import SchoolAdmin from "./pages/SchoolAdmin";
import ParentPortal from "./pages/ParentPortal";
import StudentPortal from "./pages/StudentPortal";
import StaffPortal from "./pages/StaffPortal";
import PublishingCentre from "./pages/PublishingCentre";
import BoardPack from "./pages/BoardPack";
import ReportArchive from "./pages/ReportArchive";
import Credentials from "./pages/Credentials";
import Distribution from "./pages/Distribution";
import ProgressIntelligence from "./pages/ProgressIntelligence";
import DocumentDesigner from "./pages/DocumentDesigner";
import VerifyPortal from "./pages/VerifyPortal";
import DigitalPassport from "./pages/DigitalPassport";
import ParentFinance from "./pages/ParentFinance";
import DesignStudio from "./pages/DesignStudio";
import BlueprintLibrary from "./pages/BlueprintLibrary";
import Health360 from "./pages/Health360";
import ParentVault from "./pages/ParentVault";
import AcademicHub from "./pages/academic360/AcademicHub";
import TeacherWorkbench from "./pages/academic360/TeacherWorkbench";
import { MarkBooksList, MarkBookEntry } from "./pages/academic360/MarkBook";
import GradeBook from "./pages/academic360/GradeBook";
import Promotion from "./pages/academic360/Promotion";
import Moderation from "./pages/academic360/Moderation";
import Homework360 from "./pages/academic360/Homework360";
import CurriculumStudio from "./pages/academic360/CurriculumStudio";

// Routes a parent may access. Anything else redirects to the ParentPortal.
const PARENT_ALLOWED = new Set([
  "parent", "parent/finance", "notifications", "documents", "vault",
  "passport", "student",
]);

function ProtectedShell() {
  const { user, loading } = useAuth();
  const { school } = useParams();
  const location = useLocation();
  if (loading) return <div className="edu360-root p-12 text-slate-500">Loading…</div>;
  if (!user) {
    return <Navigate to={`/education360/${school}`} replace state={{ from: location.pathname }} />;
  }
  // Parent RBAC gate — parents cannot access admin/academic modules.
  if (user.role === "parent") {
    const prefix = `/education360/${school}/`;
    const sub = location.pathname.startsWith(prefix)
      ? location.pathname.slice(prefix.length).split("/")[0]
      : "";
    const path2 = location.pathname.startsWith(prefix)
      ? location.pathname.slice(prefix.length).split("?")[0]
      : "";
    const allowed = PARENT_ALLOWED.has(sub) || PARENT_ALLOWED.has(path2)
                     || path2.startsWith("passport/");
    if (!allowed) {
      return <Navigate to={`/education360/${school}/parent`} replace />;
    }
  }
  return <PortalLayout />;
}

function SchoolRoutes() {
  const { school } = useParams();
  return (
    <AuthProvider tenantSlug={school}>
      <div className="edu360-root">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route index element={<SchoolLogin />} />
          <Route path="login" element={<SchoolLogin />} />
          <Route element={<ProtectedShell />}>
            <Route path="executive" element={<Executive />} />
            <Route path="intelligence" element={<ExecutiveIntelligence />} />
            <Route path="studentlife" element={<StudentLife360 />} />
            <Route path="ai" element={<AIExecutive />} />
            <Route path="students" element={<Students />} />
            <Route path="students/:id" element={<StudentDetail />} />
            <Route path="parents" element={<Students defaultTab="parents" />} />
            <Route path="academics" element={<Academics />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="timetable" element={<Timetable />} />
            <Route path="assessments" element={<Assessments />} />
            <Route path="reports" element={<ReportCards />} />
            <Route path="finance" element={<Finance />} />
            <Route path="admissions" element={<AdmissionsHub />} />
            <Route path="admissions-workspace" element={<AdmissionsWorkspace />} />
            <Route path="clinic" element={<Clinic />} />
            <Route path="admissions/pipeline" element={<AdmissionsPipeline />} />
            <Route path="admissions/enquiries" element={<EnquiriesInbox />} />
            <Route path="admissions/applicant/:id" element={<ApplicantDetail />} />
            <Route path="academic" element={<AcademicHub />} />
            <Route path="academic/workbench" element={<TeacherWorkbench />} />
            <Route path="academic/markbooks" element={<MarkBooksList />} />
            <Route path="academic/markbook/:id" element={<MarkBookEntry />} />
            <Route path="academic/gradebook" element={<GradeBook />} />
            <Route path="academic/homework" element={<Homework360 />} />
            <Route path="academic/moderation" element={<Moderation />} />
            <Route path="academic/promotion" element={<Promotion />} />
            <Route path="academic/curriculum" element={<CurriculumStudio />} />
            <Route path="boarding" element={<Boarding />} />
            <Route path="transport" element={<Transport />} />
            <Route path="medical" element={<Medical />} />
            <Route path="behaviour" element={<Behaviour />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="sports" element={<Sports />} />
            <Route path="hr" element={<HR />} />
            <Route path="staff-directory" element={<StaffDirectory />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="documents" element={<Documents />} />
            <Route path="school-admin" element={<SchoolAdmin />} />
            <Route path="publishing" element={<PublishingCentre />} />
            <Route path="board-pack" element={<BoardPack />} />
            <Route path="archive" element={<ReportArchive />} />
            <Route path="credentials" element={<Credentials />} />
            <Route path="distribution" element={<Distribution />} />
            <Route path="progress" element={<ProgressIntelligence />} />
            <Route path="designer" element={<DocumentDesigner />} />
            <Route path="vault" element={<ParentVault />} />
            <Route path="parent" element={<ParentPortal />} />
            <Route path="parent/finance" element={<ParentFinance />} />
            <Route path="passport" element={<DigitalPassport />} />
            <Route path="passport/:id" element={<DigitalPassport />} />
            <Route path="design-studio" element={<DesignStudio />} />
            <Route path="design-studio/blueprints" element={<BlueprintLibrary />} />
            <Route path="health" element={<Health360 />} />
            <Route path="student" element={<StudentPortal />} />
            <Route path="staff" element={<StaffPortal />} />
          </Route>
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default function Education360App() {
  return (
    <Routes>
      <Route index element={<div className="edu360-root"><Toaster position="top-right" richColors /><Education360Landing /></div>} />
      <Route path="verify" element={<VerifyPortal />} />
      <Route path="verify/:verificationId" element={<VerifyPortal />} />
      <Route path="verify/passport/:passportId" element={<VerifyPortal />} />
      <Route path="apply/:school/enquire" element={<PublicEnquiry />} />
      <Route path="apply/:school" element={<PublicApply />} />
      <Route path="applicant/:school" element={<ApplicantPortal />} />
      <Route path=":school/*" element={<SchoolRoutes />} />
    </Routes>
  );
}
